import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  TablePagination,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../utils/api';
import PattaCertificate from '../components/PattaCertificate';
import LoginDialog from '../components/LoginDialog';

const DownloadPatta = () => {
  const { user, token } = useAuth();
  const { lang } = useLanguage();
  const printRef = useRef();

  // State Management
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openLoginDialog, setOpenLoginDialog] = useState(!user);
  const [previewCertificate, setPreviewCertificate] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter states (for Admin only)
  const [filterYear, setFilterYear] = useState('');
  const [filterName, setFilterName] = useState('');
  const [availableYears, setAvailableYears] = useState([]);

  // Labels
  const labels = {
    telugu: {
      title: 'పట్టా డౌన్‌లోడ్',
      noCertificates: 'పట్టాలు లేవు',
      noData: 'డేటా లేదు',
      loading: 'లోడ్ చేస్తోంది...',
      certificateId: 'సర్టిఫికెట్ ID',
      vatuName: 'విద్యార్థి పేరు',
      guruvuName: 'గురువు పేరు',
      kramantham: 'క్రమాంతం',
      issueDate: 'జారీ తేదీ',
      actions: 'చర్యలు',
      preview: 'ప్రివ్యూ',
      download: 'డౌన్‌లోడ్',
      print: 'ప్రింట్',
      filterByYear: 'సంవత్సరం ఆధారంగా ఫిల్టర్ చేయండి',
      filterByName: 'పేరు ఆధారంగా ఫిల్టర్ చేయండి',
      allYears: 'అన్ని సంవత్సరాలు',
      pleaseLogin: 'దయచేసి లాగిన్ చేయండి',
      loginRequired: 'పట్టాలను చూడటానికి దయచేసి లాగిన్ చేయండి',
    },
    sanskrit: {
      title: 'पट्टा अवतारणम्',
      noCertificates: 'पट्टाः न सन्ति',
      noData: 'आंकड़े न सन्ति',
      loading: 'लोडयते...',
      certificateId: 'प्रमाणपत्र संख्या',
      vatuName: 'वटु नाम',
      guruvuName: 'गुरु नाम',
      kramantham: 'क्रमान्तम्',
      issueDate: 'निर्गम तिथिः',
      actions: 'कर्मणि',
      preview: 'पूर्वावलोकनम्',
      download: 'अवतारणम्',
      print: 'मुद्रणम्',
      filterByYear: 'संवत्सरेण पुट्टिकरणम्',
      filterByName: 'नाम्ना पुट्टिकरणम्',
      allYears: 'सर्वे संवत्सराः',
      pleaseLogin: 'कृपया लॉगिन् करतु',
      loginRequired: 'पट्टां दृष्टुम् कृपया लॉगिन् करतु',
    },
  };

  const currentLabels = labels[lang] || labels.telugu;

  // Fetch certificates based on user role
  useEffect(() => {
    if (user) {
      fetchCertificates();
    }
  }, [user]);

  // Fetch available years for admin
  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAvailableYears();
    }
  }, [user]);

  const fetchAvailableYears = async () => {
    try {
      const response = await api.get('/api/admin-config/all-years');
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const years = result.data.sort((a, b) => b.year_id - a.year_id);
          setAvailableYears(years);
        }
      }
    } catch (err) {
      console.error('Error fetching years:', err);
    }
  };

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      setError('');

      let response;
      let filteredCerts = [];

      if (user.role === 'Admin') {
        // Admin: fetch all certificates
        response = await api.get('/api/certificate/all');
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            filteredCerts = result.data || [];
          }
        }
      } else if (user.role === 'PathasalaAdmin') {
        // PathasalaAdmin: fetch certificates for their pathasala
        response = await api.get(
          `/api/certificate/by-pathasala/${user.pathasala_id}`
        );
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            filteredCerts = result.data || [];
          }
        }
      } else {
        // Regular user: fetch certificates matching their phone/email
        const userIdentifier = user.phone || user.email;
        if (!userIdentifier) {
          setError('User phone or email not found');
          setLoading(false);
          return;
        }

        response = await api.get(
          `/api/certificate/by-user/${userIdentifier}`
        );
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            filteredCerts = result.data || [];
          }
        }
      }

      // Sort by issued date ascending
      filteredCerts.sort((a, b) => {
        const dateA = new Date(a.issue_date || 0);
        const dateB = new Date(b.issue_date || 0);
        return dateA - dateB;
      });

      setCertificates(filteredCerts);
    } catch (err) {
      console.error('Error fetching certificates:', err);
      setError(
        err.message || 'Failed to fetch certificates. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle preview
  const handlePreview = async (certificateId) => {
    try {
      setLoadingPreview(true);
      const response = await api.get(
        `/api/certificate/with-signatures/${certificateId}`
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setPreviewCertificate(result.data);
          setOpenPreview(true);
        }
      }
    } catch (err) {
      console.error('Error loading certificate preview:', err);
      setError('Failed to load certificate preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Handle download PDF
  const handleDownloadPDF = async (certificateId) => {
    try {
      setLoadingPreview(true);

      // Fetch certificate if not already in preview
      if (!previewCertificate || previewCertificate.certificate_id !== certificateId) {
        const response = await api.get(
          `/api/certificate/with-signatures/${certificateId}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch certificate');
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error('Invalid certificate data');
        }

        setPreviewCertificate(result.data);
      }

      // Wait for state update, then trigger PDF download
      setTimeout(() => {
        const element = printRef.current;
        if (element) {
          const html2canvas = window.html2canvas;
          if (html2canvas) {
            html2canvas(element, {
              scale: 2,
              useCORS: true,
              logging: false,
              backgroundColor: '#ffffff',
            }).then(canvas => {
              const link = document.createElement('a');
              link.href = canvas.toDataURL('image/png');
              link.download = `patta_${certificateId}.png`;
              link.click();
            });
          }
        }
      }, 500);
    } catch (err) {
      console.error('Error downloading certificate:', err);
      setError('Failed to download certificate');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Handle print
  const handlePrint = () => {
    if (printRef.current) {
      const printWindow = window.open('', '', 'width=800,height=600');
      printWindow.document.write(printRef.current.innerHTML);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // Filter certificates for admin
  const getFilteredCertificates = () => {
    let filtered = certificates;

    if (filterYear) {
      filtered = filtered.filter(cert => cert.year_id === parseInt(filterYear));
    }

    if (filterName) {
      const searchTerm = filterName.toLowerCase();
      filtered = filtered.filter(
        cert =>
          (cert.vatu_name?.toLowerCase().includes(searchTerm)) ||
          (cert.guruvu_name?.toLowerCase().includes(searchTerm))
      );
    }

    return filtered;
  };

  const displayCertificates = user?.role === 'admin' ? getFilteredCertificates() : certificates;
  const totalRows = displayCertificates.length;

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedCertificates = displayCertificates.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Show login dialog if not authenticated
  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            {currentLabels.pleaseLogin}
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
            {currentLabels.loginRequired}
          </Typography>
        </Paper>
        <LoginDialog
          open={openLoginDialog}
          onClose={() => setOpenLoginDialog(false)}
          onLoginSuccess={() => {
            setOpenLoginDialog(false);
            // The user state will be updated via AuthContext, triggering the useEffect
          }}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          {currentLabels.title}
        </Typography>
      </Box>

      {/* Admin Filters */}
      {user?.role === 'admin' && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{currentLabels.filterByYear}</InputLabel>
                <Select
                  label={currentLabels.filterByYear}
                  value={filterYear}
                  onChange={(e) => {
                    setFilterYear(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">{currentLabels.allYears}</MenuItem>
                  {availableYears.map(year => (
                    <MenuItem key={year._id} value={year.year_id}>
                      {year.year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={currentLabels.filterByName}
                placeholder="Search by name..."
                value={filterName}
                onChange={(e) => {
                  setFilterName(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : totalRows === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">{currentLabels.noCertificates}</Typography>
        </Paper>
      ) : (
        <>
          {/* Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {currentLabels.certificateId}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {currentLabels.vatuName}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {currentLabels.guruvuName}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {currentLabels.kramantham}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {currentLabels.issueDate}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>
                    {currentLabels.actions}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCertificates.map((cert) => (
                  <TableRow key={cert._id} hover>
                    <TableCell>{cert.certificate_id}</TableCell>
                    <TableCell>{cert.vatu_name}</TableCell>
                    <TableCell>{cert.guruvu_name}</TableCell>
                    <TableCell>{cert.kramantham_display || cert.kramantham_type}</TableCell>
                    <TableCell>
                      {cert.issue_date
                        ? new Date(cert.issue_date).toLocaleDateString()
                        : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Tooltip title={currentLabels.preview}>
                        <IconButton
                          size="small"
                          onClick={() => handlePreview(cert.certificate_id)}
                          disabled={loadingPreview}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={currentLabels.download}>
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadPDF(cert.certificate_id)}
                          disabled={loadingPreview}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={currentLabels.print}>
                        <IconButton
                          size="small"
                          onClick={handlePrint}
                          disabled={!previewCertificate}
                        >
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {/* Preview Dialog */}
      <Dialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="lg" fullWidth>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1,
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="h6" sx={{ m: 0 }}>
            {currentLabels.preview}
          </Typography>
          <Box>
            <Tooltip title={currentLabels.print}>
              <IconButton onClick={handlePrint} sx={{ mr: 1 }}>
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={currentLabels.download}>
              <IconButton
                onClick={() =>
                  handleDownloadPDF(previewCertificate?.certificate_id)
                }
                sx={{ mr: 1 }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={() => setOpenPreview(false)}>×</IconButton>
          </Box>
        </Box>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          {previewCertificate ? (
            <div ref={printRef} style={{ margin: 0, padding: 0, width: 'fit-content' }}>
              <PattaCertificate data={previewCertificate} />
            </div>
          ) : (
            <CircularProgress />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default DownloadPatta;
